import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  FolderTree, 
  File, 
  Folder, 
  FolderOpen,
  Upload, 
  Download,
  Trash2,
  Edit,
  Plus,
  Search,
  Home,
  ArrowLeft,
  RefreshCw,
  Save,
  X,
  FileText,
  Image as ImageIcon,
  FileCode,
  FileArchive,
  FileVideo,
  FileAudio,
  AlertCircle,
  ChevronRight,
  HardDrive,
  Copy,
  Clipboard
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../ui/context-menu';
import api from '../../utils/api';

const FileExplorerTab = ({ agentId }) => {
  const [currentPath, setCurrentPath] = useState('C:\\');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPattern, setSearchPattern] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [fileContent, setFileContent] = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pathHistory, setPathHistory] = useState(['C:\\']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const fileInputRef = useRef(null);

  // Load initial directory
  useEffect(() => {
    if (agentId) {
      loadDirectory(currentPath);
    }
  }, [agentId]);

  // Load directory contents
  const loadDirectory = useCallback(async (path, addToHistory = true) => {
    setLoading(true);
    setError(null);
    setSelectedFiles([]);
    
    try {
      const response = await api.post(`/agents/${agentId}/file-explorer/list`, { path });
      
      // Sort files: folders first, then files, alphabetically
      const sortedFiles = (response.data.files || []).sort((a, b) => {
        if (a.isDirectory && !b.isDirectory) return -1;
        if (!a.isDirectory && b.isDirectory) return 1;
        return a.name.localeCompare(b.name);
      });
      
      setFiles(sortedFiles);
      setCurrentPath(path);
      
      // Update history
      if (addToHistory) {
        const newHistory = pathHistory.slice(0, historyIndex + 1);
        newHistory.push(path);
        setPathHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load directory';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Load directory error:', err);
    } finally {
      setLoading(false);
    }
  }, [agentId, pathHistory, historyIndex]);

  // Navigate to path
  const navigateToPath = useCallback((path) => {
    loadDirectory(path, true);
  }, [loadDirectory]);

  // Navigate up one level
  const navigateUp = useCallback(() => {
    const parts = currentPath.split('\\').filter(p => p);
    if (parts.length > 1) {
      const parentPath = parts.slice(0, -1).join('\\') + '\\';
      navigateToPath(parentPath);
    } else if (parts.length === 1) {
      navigateToPath(parts[0] + '\\');
    }
  }, [currentPath, navigateToPath]);

  // Navigate back in history
  const navigateBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      loadDirectory(pathHistory[newIndex], false);
    }
  }, [historyIndex, pathHistory, loadDirectory]);

  // Navigate forward in history
  const navigateForward = useCallback(() => {
    if (historyIndex < pathHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      loadDirectory(pathHistory[newIndex], false);
    }
  }, [historyIndex, pathHistory, loadDirectory]);

  // Handle file/folder double click
  const handleFileDoubleClick = useCallback(async (file) => {
    if (file.isDirectory) {
      navigateToPath(file.path);
    } else {
      await viewFile(file);
    }
  }, [navigateToPath]);

  // Handle file selection
  const handleFileClick = useCallback((file, e) => {
    if (e.ctrlKey || e.metaKey) {
      setSelectedFiles(prev => {
        const isSelected = prev.some(f => f.path === file.path);
        if (isSelected) {
          return prev.filter(f => f.path !== file.path);
        }
        return [...prev, file];
      });
    } else {
      setSelectedFiles([file]);
    }
    setSelectedFile(file);
  }, []);

  // View/edit file
  const viewFile = useCallback(async (file) => {
    setFileLoading(true);
    setSelectedFile(file);
    
    try {
      const response = await api.post(`/agents/${agentId}/file-explorer/read`, { path: file.path });
      setFileContent(response.data.content || '');
      setEditDialogOpen(true);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to read file';
      toast.error(errorMsg);
    } finally {
      setFileLoading(false);
    }
  }, [agentId]);

  // Save file
  const saveFile = useCallback(async () => {
    if (!selectedFile) return;

    setFileLoading(true);
    try {
      await api.post(`/agents/${agentId}/file-explorer/write`, {
        path: selectedFile.path,
        content: fileContent
      });
      toast.success('File saved successfully');
      setEditDialogOpen(false);
      loadDirectory(currentPath, false);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save file';
      toast.error(errorMsg);
    } finally {
      setFileLoading(false);
    }
  }, [agentId, selectedFile, fileContent, currentPath, loadDirectory]);

  // Delete file/folder
  const deleteFile = useCallback(async (file) => {
    setFileLoading(true);
    try {
      await api.post(`/agents/${agentId}/file-explorer/delete`, { path: file.path });
      toast.success(`${file.isDirectory ? 'Folder' : 'File'} deleted successfully`);
      loadDirectory(currentPath, false);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to delete';
      toast.error(errorMsg);
    } finally {
      setFileLoading(false);
      setDeleteConfirmOpen(false);
    }
  }, [agentId, currentPath, loadDirectory]);

  // Rename file/folder
  const renameFile = useCallback(async () => {
    if (!selectedFile || !renameValue) return;

    const pathParts = selectedFile.path.split('\\');
    pathParts[pathParts.length - 1] = renameValue;
    const newPath = pathParts.join('\\');

    setFileLoading(true);
    try {
      await api.post(`/agents/${agentId}/file-explorer/rename`, {
        oldPath: selectedFile.path,
        newPath: newPath
      });
      toast.success('Renamed successfully');
      setRenameDialogOpen(false);
      loadDirectory(currentPath, false);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to rename';
      toast.error(errorMsg);
    } finally {
      setFileLoading(false);
    }
  }, [agentId, selectedFile, renameValue, currentPath, loadDirectory]);

  // Create folder
  const createFolder = useCallback(async () => {
    if (!newFolderName) return;

    const newPath = currentPath + (currentPath.endsWith('\\') ? '' : '\\') + newFolderName;

    setFileLoading(true);
    try {
      await api.post(`/agents/${agentId}/file-explorer/create-folder`, { path: newPath });
      toast.success('Folder created successfully');
      setNewFolderDialogOpen(false);
      setNewFolderName('');
      loadDirectory(currentPath, false);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to create folder';
      toast.error(errorMsg);
    } finally {
      setFileLoading(false);
    }
  }, [agentId, currentPath, newFolderName, loadDirectory]);

  // Download file
  const downloadFile = useCallback(async (file) => {
    setFileLoading(true);
    try {
      const response = await api.post(`/agents/${agentId}/file-explorer/download`, 
        { path: file.path },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('File downloaded successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to download file';
      toast.error(errorMsg);
    } finally {
      setFileLoading(false);
    }
  }, [agentId]);

  // Upload file
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const uploadPath = currentPath + (currentPath.endsWith('\\') ? '' : '\\') + file.name;

    setFileLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', uploadPath);

      await api.post(`/agents/${agentId}/file-explorer/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      loadDirectory(currentPath, false);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload file';
      toast.error(errorMsg);
    } finally {
      setFileLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [agentId, currentPath, loadDirectory]);

  // Search files
  const searchFiles = useCallback(async () => {
    if (!searchPattern.trim()) {
      loadDirectory(currentPath, false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/agents/${agentId}/file-explorer/search`, {
        path: currentPath,
        pattern: searchPattern
      });
      setFiles(response.data.files || []);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to search files';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [agentId, currentPath, searchPattern, loadDirectory]);

  // Copy path to clipboard
  const copyPath = useCallback((path) => {
    navigator.clipboard.writeText(path);
    toast.success('Path copied to clipboard');
  }, []);

  // Get file icon based on type
  const getFileIcon = useCallback((file) => {
    if (file.isDirectory) {
      return <Folder className="w-5 h-5 text-yellow-500" />;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    
    if (['txt', 'log', 'md', 'rtf'].includes(ext)) {
      return <FileText className="w-5 h-5 text-gray-500" />;
    }
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cs', 'cpp', 'c', 'h', 'html', 'css', 'json', 'xml', 'yml', 'yaml', 'sh', 'bat', 'ps1'].includes(ext)) {
      return <FileCode className="w-5 h-5 text-green-500" />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico', 'webp'].includes(ext)) {
      return <ImageIcon className="w-5 h-5 text-purple-500" />;
    }
    if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) {
      return <FileArchive className="w-5 h-5 text-orange-500" />;
    }
    if (['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', 'webm'].includes(ext)) {
      return <FileVideo className="w-5 h-5 text-red-500" />;
    }
    if (['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'].includes(ext)) {
      return <FileAudio className="w-5 h-5 text-pink-500" />;
    }

    return <File className="w-5 h-5 text-gray-400" />;
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (!bytes || bytes === 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  }, []);

  // Parse breadcrumb from path
  const getBreadcrumbs = useCallback(() => {
    const parts = currentPath.split('\\').filter(p => p);
    const breadcrumbs = [];
    let path = '';
    
    for (let i = 0; i < parts.length; i++) {
      path += parts[i] + '\\';
      breadcrumbs.push({
        name: parts[i],
        path: path,
        isLast: i === parts.length - 1
      });
    }
    
    return breadcrumbs;
  }, [currentPath]);

  return (
    <div className="space-y-4">
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderTree className="w-5 h-5 text-blue-600" />
              File Explorer
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                onClick={navigateBack}
                variant="outline"
                size="sm"
                disabled={historyIndex <= 0 || loading}
                title="Back"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => navigateToPath('C:\\')}
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={loading}
              >
                <HardDrive className="w-4 h-4" />
                C:\
              </Button>
              <Button
                onClick={navigateUp}
                variant="outline"
                size="sm"
                disabled={loading || currentPath === 'C:\\'}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Up
              </Button>
              <Button
                onClick={() => loadDirectory(currentPath, false)}
                variant="outline"
                size="sm"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Toolbar */}
          <div className="mb-3 flex flex-wrap gap-2">
            <Button
              onClick={() => setNewFolderDialogOpen(true)}
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={loading}
            >
              <Plus className="w-4 h-4" />
              New Folder
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={loading}
            >
              <Upload className="w-4 h-4" />
              Upload
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
            />

            {/* Search */}
            <div className="ml-auto flex gap-2">
              <Input
                placeholder="Search files..."
                value={searchPattern}
                onChange={(e) => setSearchPattern(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchFiles()}
                className="w-48 h-9"
              />
              <Button onClick={searchFiles} size="sm" variant="outline" disabled={loading}>
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Breadcrumb Navigation */}
          <div className="mb-3 p-2 bg-gray-50 rounded-lg border flex items-center gap-1 overflow-x-auto">
            <HardDrive className="w-4 h-4 text-gray-400 shrink-0" />
            {getBreadcrumbs().map((crumb, index) => (
              <div key={crumb.path} className="flex items-center shrink-0">
                {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
                <button
                  onClick={() => !crumb.isLast && navigateToPath(crumb.path)}
                  className={`px-2 py-1 rounded text-sm ${
                    crumb.isLast 
                      ? 'font-semibold text-gray-800 bg-white border' 
                      : 'text-blue-600 hover:bg-blue-50'
                  }`}
                  disabled={crumb.isLast}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7"
              onClick={() => copyPath(currentPath)}
              title="Copy path"
            >
              <Copy className="w-3 h-3" />
            </Button>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => loadDirectory(currentPath, false)}
                className="ml-auto text-red-700 hover:bg-red-100"
              >
                Retry
              </Button>
            </div>
          )}

          {/* File List */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Size</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Modified</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-12 text-center text-gray-500">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-500" />
                        Loading directory...
                      </td>
                    </tr>
                  ) : files.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-12 text-center text-gray-500">
                        <Folder className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        {searchPattern ? 'No files found matching search' : 'This folder is empty'}
                      </td>
                    </tr>
                  ) : (
                    files.map((file, index) => (
                      <ContextMenu key={file.path || index}>
                        <ContextMenuTrigger>
                          <tr
                            className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                              selectedFiles.some(f => f.path === file.path) ? 'bg-blue-50' : ''
                            }`}
                            onClick={(e) => handleFileClick(file, e)}
                            onDoubleClick={() => handleFileDoubleClick(file)}
                          >
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {getFileIcon(file)}
                                <span className="text-sm font-medium text-gray-700 truncate max-w-md">
                                  {file.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {file.isDirectory ? '-' : formatFileSize(file.size)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-500">
                              {formatDate(file.modified)}
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex gap-1">
                                {!file.isDirectory && (
                                  <>
                                    <Button
                                      onClick={(e) => { e.stopPropagation(); viewFile(file); }}
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      title="Edit"
                                    >
                                      <Edit className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button
                                      onClick={(e) => { e.stopPropagation(); downloadFile(file); }}
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      title="Download"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedFile(file);
                                    setDeleteConfirmOpen(true);
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        </ContextMenuTrigger>
                        <ContextMenuContent>
                          {file.isDirectory ? (
                            <ContextMenuItem onClick={() => navigateToPath(file.path)}>
                              <FolderOpen className="w-4 h-4 mr-2" />
                              Open
                            </ContextMenuItem>
                          ) : (
                            <>
                              <ContextMenuItem onClick={() => viewFile(file)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </ContextMenuItem>
                              <ContextMenuItem onClick={() => downloadFile(file)}>
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </ContextMenuItem>
                            </>
                          )}
                          <ContextMenuSeparator />
                          <ContextMenuItem onClick={() => copyPath(file.path)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy Path
                          </ContextMenuItem>
                          <ContextMenuItem
                            onClick={() => {
                              setSelectedFile(file);
                              setRenameValue(file.name);
                              setRenameDialogOpen(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Rename
                          </ContextMenuItem>
                          <ContextMenuSeparator />
                          <ContextMenuItem
                            onClick={() => {
                              setSelectedFile(file);
                              setDeleteConfirmOpen(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Status Bar */}
          <div className="mt-2 px-2 py-1 bg-gray-50 rounded border text-xs text-gray-500 flex items-center justify-between">
            <span>{files.length} item(s)</span>
            {selectedFiles.length > 0 && (
              <span>{selectedFiles.length} selected</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit File Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              {selectedFile?.name}
            </DialogTitle>
            <DialogDescription className="text-xs font-mono truncate">
              {selectedFile?.path}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <Textarea
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
              className="w-full h-[450px] font-mono text-sm resize-none"
              placeholder="File content..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveFile} disabled={fileLoading}>
              {fileLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Create New Folder
            </DialogTitle>
            <DialogDescription>
              Create in: {currentPath}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              onKeyDown={(e) => e.key === 'Enter' && createFolder()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createFolder} disabled={fileLoading || !newFolderName.trim()}>
              {fileLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Rename {selectedFile?.isDirectory ? 'Folder' : 'File'}
            </DialogTitle>
            <DialogDescription>
              Current: {selectedFile?.name}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="New name"
              onKeyDown={(e) => e.key === 'Enter' && renameFile()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={renameFile} disabled={fileLoading || !renameValue.trim()}>
              {fileLoading ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete?
              <br />
              <span className="font-semibold text-gray-800">{selectedFile?.name}</span>
              {selectedFile?.isDirectory && (
                <span className="block mt-2 text-red-600 font-medium">
                  ⚠️ This will delete the folder and all its contents!
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteFile(selectedFile)}
              disabled={fileLoading}
            >
              {fileLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileExplorerTab;
